import PieC from '@components/ui/charts/radial';
import router from '@configs/router';
import { useAppDispatch, useAppSelector } from '@configs/store/hooks';
import { gray } from '@configs/theme/theme';
import Routes from '@constants/routes.enum';
import {
  Box,
  Container,
  MenuItem,
  Select,
  useColorScheme,
} from '@mui/material';
import { rootRoute } from '@rootScreen';
import { createRoute } from '@tanstack/react-router';
import { getItem, setItem } from '@utils/localStorage';
import classNames from 'classnames';
import moment from 'moment';
import { number } from 'motion/react';
import { JSX, JSXElementConstructor, useEffect, useLayoutEffect, useState } from 'react';
import { LuLoaderCircle } from 'react-icons/lu';
import Line from '@components/ui/charts/Line';

type inputs = {
  [k in
    | 'stake'
    | 'monthly_operations_cost'
    | 'network_apy'
    | 'type'
    | 'standBy_active_ratio']: string | number;
};
const Nodes = () => {
  const { mode, systemMode } = useColorScheme();
  const shmPrice = useAppSelector((state) => state?.shm?.price);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<inputs>({} as inputs);
  const [saRatio, setSaRatio] = useState<any[]>([]);
  const [projectionData, setprojectionData] = useState<any[]>([]);
  const [results, setResults] = useState<any>(undefined);

  useEffect(() => {
    if (shmPrice) {
      const ls = getItem('nodes');
      if (ls && ls['stake']) {
        setData(ls);
        setLoading(true);
        let calculations = calculateRewards(ls);
        setResults(calculations);
        setprojectionData(generateProjectionData(calculations));
        setSaRatio(generateSARatioData());
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    }
  }, [shmPrice]);

  const calculateActiveTime = (saRatio) => {
    return saRatio / 10; // Formula: 1 / (S + A) where A = 1
  };

  const calculateRewards = (_data: inputs) => {
    const activeTimePercent = calculateActiveTime(
      Number(_data.standBy_active_ratio)
    );

    const effectiveAPY = (Number(_data.network_apy) * activeTimePercent) / 100;

    // Annual rewards calculation
    const annualRewardsSHM = Number(_data.stake) * effectiveAPY;
    const annualRewardsUSD = annualRewardsSHM * shmPrice;

    // Monthly breakdown
    const monthlyRewardsSHM = annualRewardsSHM / 12;
    const monthlyRewardsUSD = annualRewardsUSD / 12;

    // Operating costs
    const annualOperatingCosts = Number(_data.monthly_operations_cost) * 12;
    const totalInitialInvestment = Number(_data.stake);

    // Net calculations
    const netAnnualReturn = annualRewardsUSD - annualOperatingCosts;
    const netMonthlyReturn = netAnnualReturn / 12;

    const archiverMultiplier = data.type === 'Archive' ? 3.5 : 1;
    // ROI calculations
    const realizedAPY =
      ((archiverMultiplier * netAnnualReturn) / totalInitialInvestment) * 100;
    const monthsToROI =
      (archiverMultiplier * totalInitialInvestment) /
      Math.max(netMonthlyReturn, 1);

    let res = {
      activeTimePercent: activeTimePercent * 100,
      effectiveAPY: effectiveAPY * 100,
      annualRewardsSHM: annualRewardsSHM * archiverMultiplier,
      annualRewardsUSD: annualRewardsUSD * archiverMultiplier,
      monthlyRewardsSHM: monthlyRewardsSHM * archiverMultiplier,
      monthlyRewardsUSD: monthlyRewardsUSD * archiverMultiplier,
      annualOperatingCosts,
      totalInitialInvestment,
      netAnnualReturn:
        annualRewardsUSD * archiverMultiplier - annualOperatingCosts,
      netMonthlyReturn:
        (annualRewardsUSD * archiverMultiplier - annualOperatingCosts) / 12,
      realizedAPY,
      monthsToROI,
      archiverMultiplier,
    };
    return res;
  };

  const generateProjectionData = (calculations) => {
    return Array.from({ length: 10 }, (_, i) => {
      const month = i + 1;
      const cumulativeRewards = calculations.monthlyRewardsUSD * month;
      const cumulativeOperatingCosts =
        Number(data.monthly_operations_cost) * month;
      const cumulativeNetProfit =
        cumulativeRewards -
        cumulativeOperatingCosts -
        calculations.totalInitialInvestment;

      return {
        month,
        grossRewards: cumulativeRewards,
        operatingCosts: cumulativeOperatingCosts,
        netProfit: cumulativeNetProfit,
        breakeven: cumulativeNetProfit >= 0,
      };
    });
  };

  const generateSARatioData = () => {
    const ratios = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5];
    return ratios.map((ratio) => {
      const activeTime = calculateActiveTime(ratio);
      const effectiveAPY = (Number(data.network_apy) * activeTime) / 100;
      const annualRewards = Number(data.stake) * effectiveAPY * shmPrice;
      const netReturn =
        annualRewards - Number(data.monthly_operations_cost) * 12;

      return {
        ratio: `${ratio}:1`,
        activeTime: activeTime * 100,
        annualRewards,
        netReturn,
        effectiveAPY: effectiveAPY * 100,
      };
    });
  };

  const nodeTypeInputs: ({
    [k in 'name' | 'helperText' | 'placeholder' | 'type']?: string;
  } & {
    key: keyof inputs;
    select?: boolean;
    options?: string[];
    optionsText?: string[];
    // @ts-ignore
    additionalText?: JSX | string;
    slider?: boolean;
    min?: number;
    max?: number;
  })[] = [
    {
      name: 'Node Type',
      helperText: 'Which type of node are you considering ',
      key: 'type',
      additionalText: '',
      select: true,
      options: ['Validator', 'Archiver'],
      optionsText: [
        'Validator nodes participate in consensus and earn standard rewards',
        'Archiver nodes earn significantly more rewards (3.5x) but require more hardware',
      ],
    },
    {
      name: 'Network APY',
      helperText: 'What is your network APY?',
      additionalText: (
        <>
          {' You can calculate your network APY from the'}
          <p
            className="underline cursor-pointer text-primary/80 hover:text-primary/60 mx-1"
            onClick={() => {
              router?.navigate({ to: '/home' });
            }}
          >
            Home
          </p>
          {' page.'}
        </>
      ),
      key: 'network_apy',
      type: 'number',
      placeholder: 'Network APY(%)',
    },
    {
      name: 'SHM Stake',
      helperText: 'How much in USD are you staking.',
      key: 'stake',
      type: 'number',
    },
    {
      name: 'Monthly Node Costs',
      key: 'monthly_operations_cost',
      helperText: 'How much (USD) do you send on running the node monthly?',
      placeholder: 'Enter ammount (USD)',
      type: 'number',
    },
    {
      name: 'Standby:Active Ratio',
      key: 'standBy_active_ratio',
      helperText:
        'Whats the ratio between when your node is active or on standby.',
      slider: true,
      min: 0,
      max: 10,
      additionalText: (
        <>
          {'Active Time:'}
          <p className="mx-2">
            ~{(calculateActiveTime(data.standBy_active_ratio) * 100).toFixed(1)}
            %
          </p>
        </>
      ),
    },
  ];

  const handleValueChange = (newValue: string | number, key: string) => {
    setData((prev) => {
      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const saveData = () => {
    setLoading(true);
    setItem('nodes', JSON.stringify(data));
    let calculations = calculateRewards(data);
    setprojectionData(generateProjectionData(calculations));
    setResults(calculations);
    setSaRatio(generateSARatioData());
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <Container
      className="m-0 px-2 !pl-2 overflow-y-scroll flex flex-col !h-full relative gap-1"
      maxWidth={false}
      sx={{ placeContent: 'start' }}
    >
      <div className="w-full h-auto min-h-[70dvh] px-1">
        <Box
          className="w-full h-auto rounded-xl"
          sx={{
            bgcolor:
              mode === 'dark' || (mode === 'system' && systemMode === 'dark')
                ? gray[800]
                : gray[100],
          }}
        >
          <div className="w-full h-auto flex flex-col py-2 md:px-4 px-5">
            <div className="w-full mb-3 text-base">
              <p className="text-xl mb-2 font-semibold">
                Calculate Your Node Rewards & ROI
              </p>
              <p className=" mx-auto">
                Estimate potential earnings from running Shardeum validator and
                archiver nodes based on the official reward system
              </p>
            </div>
            <div className="w-full h-full flex md:grid flex-col md:grid-cols-3 grid-flow-col-dense md:grid-rows-2 md:divider-1 gap-3">
              {nodeTypeInputs.map((item) => {
                return (
                  <div className="flex flex-col w-full h-auto">
                    <h4 className="text-base md:text-sm font-normal m-0 p-0">
                      {item.name}
                    </h4>
                    {item.helperText && (
                      <p className="text-xs font-light mt-1">
                        {item.helperText}
                      </p>
                    )}

                    {item.type && (
                      <input
                        className="mt-3 outline-0 border rounded-xl placeholder:text-base text-base py-2 px-3"
                        placeholder={item.placeholder || 'Enter Text here '}
                        type={item.type}
                        value={data?.[item.key]}
                        onChange={(e) =>
                          handleValueChange(e.target.value, item.key)
                        }
                      />
                    )}
                    {item.slider && (
                      <>
                        <input
                          type="range"
                          min={item.min?.toString()}
                          max={item.max?.toString()}
                          step={(0.001 * (Number(item.max) - Number(item.min)))
                            .toFixed(2)
                            .toString()}
                          value={data.standBy_active_ratio}
                          onChange={(e) =>
                            handleValueChange(
                              parseFloat(e.target.value).toFixed(1),
                              item.key
                            )
                          }
                          className="w-full rounded-lg appearance-none bg-gray-900 cursor-pointer mt-2"
                          style={{
                            background: (() => {
                              const val = Number(data.standBy_active_ratio);
                              const min = Number(item.min ?? 0);
                              const max = Number(item.max ?? 5);
                              const percent = ((val - min) / (max - min)) * 100;

                              return `linear-gradient(90deg, #1BAD3D ${percent}%,rgb(255, 191, 0) ${percent}%)`;
                            })(),
                            height: '6px',
                            outline: 'none',
                            border: 'none',
                          }}
                        />
                        <div className="flex justify-between text-xs text-primary/80 mt-1">
                          <span className="text-[#1BAD3D] font-normal ">
                            Active
                          </span>
                          <span className="font-bold">
                            {data.standBy_active_ratio}:
                            {(10 - Number(data?.standBy_active_ratio)).toFixed(
                              1
                            )}
                          </span>
                          <span className="text-[#FFBF00] font-normal ">
                            Stand By
                          </span>
                        </div>
                      </>
                    )}
                    {item.select && (
                      <div className="w-full h-auto border rounded-xl mt-3 px-3 py-2">
                        <select
                          className="outline-0 placeholder:text-base text-base w-full"
                          value={data?.[item.key]}
                          onChange={(e) => {
                            console.log(e.target.value);
                            handleValueChange(
                              e.target.value.toString(),
                              item.key
                            );
                          }}
                        >
                          {item.options?.map((opt) => {
                            return (
                              <option
                                value={opt}
                                style={{
                                  backgroundColor:
                                    mode === 'dark' ||
                                    (mode === 'system' && systemMode === 'dark')
                                      ? gray[900]
                                      : gray[300],
                                }}
                              >
                                {opt}
                              </option>
                            );
                          })}
                        </select>
                        <p className="text-xs font-light mt-1 opacity-70">
                          {
                            item?.optionsText![
                              item?.options?.findIndex((i) => i === data?.type)!
                            ]
                          }
                        </p>
                      </div>
                    )}
                    {item.additionalText && (
                      <p className="text-xs font-light opacity-90 flex mt-2">
                        {item.additionalText}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              onClick={saveData}
              className="bg-primary rounded-lg px-5 py-3 text-sm font-semibold cursor-pointer w-full md:w-auto h-auto md:ml-auto mt-6 !text-white mb-3"
            >
              Submit
            </button>
          </div>
        </Box>
      </div>
      {results && (
        <div className="w-full h-auto px-1">
          <Box
            className="w-full h-auto rounded-xl"
            sx={{
              bgcolor:
                mode === 'dark' || (mode === 'system' && systemMode === 'dark')
                  ? gray[800]
                  : gray[100],
            }}
          >
            {!loading && (
              <div className="w-full min-h-[70dvh] flex flex-col gap-3 py-4 md:px-4 px-5">
                <div className="w-full h-auto">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <span className="bg-gradient-to-r from-green-400 to-emerald-400 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-slate-900 mr-3">
                      📊
                    </span>
                    {data.type === 'Archive' ? 'Archiver' : 'Validator'}
                    Node Results
                  </h2>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 p-4 rounded-xl border border-green-500/30">
                      <div className="text-green-300 text-sm font-medium">
                        Effective APY
                      </div>
                      <div className="text-2xl font-bold ">
                        {results?.effectiveAPY?.toFixed(2)}%
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 p-4 rounded-xl border border-blue-500/30">
                      <div className="text-blue-300 text-sm font-medium">
                        Realized APY
                      </div>
                      <div className="text-2xl font-bold ">
                        {results?.realizedAPY?.toFixed(2)}%
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-4 rounded-xl border border-purple-500/30">
                      <div className="text-purple-300 text-sm font-medium">
                        Active Time
                      </div>
                      <div className="text-2xl font-bold ">
                        {results?.activeTimePercent?.toFixed(1)}%
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-4 rounded-xl border border-orange-500/30">
                      <div className="text-orange-300 text-sm font-medium">
                        ROI Time
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {results?.monthsToROI?.toFixed(1)}mo
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">
                        Monthly Earnings
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-blue-300">
                            Gross Rewards (SHM)
                          </span>
                          <span className="font-bold text-cyan-400">
                            {results.monthlyRewardsSHM?.toFixed(2)} SHM
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-300">
                            Gross Rewards (USD)
                          </span>
                          <span className="font-bold text-cyan-400">
                            ${results.monthlyRewardsUSD?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-300">Operating Costs</span>
                          <span className="font-bold text-red-400">
                            -${data.monthly_operations_cost}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-white/20 pt-2">
                          <span className="text-white font-medium">
                            Net Monthly Profit
                          </span>
                          <span className="font-bold text-green-400">
                            ${results.netMonthlyReturn?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">
                        Annual Summary
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-blue-300">
                            Annual Rewards (SHM)
                          </span>
                          <span className="font-bold text-cyan-400">
                            {results.annualRewardsSHM?.toFixed(2)} SHM
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-300">
                            Annual Rewards (USD)
                          </span>
                          <span className="font-bold text-cyan-400">
                            ${results.annualRewardsUSD?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-300">
                            Annual Operating Costs
                          </span>
                          <span className="font-bold text-red-400">
                            -${results.annualOperatingCosts}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-white/20 pt-2">
                          <span className="text-white font-medium">
                            Net Annual Return
                          </span>
                          <span className="font-bold text-green-400">
                            ${results.netAnnualReturn?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {data.type === 'Archive' && (
                    <div className="mt-6 p-4 ">
                      <p className=" text-sm">
                        <strong>Archiver Node Bonus:</strong> Rewards multiplied
                        by {results.archiverMultiplier}x due to critical role in
                        preserving network history and higher hardware
                        requirements.
                      </p>
                    </div>
                  )}
                </div>
                {projectionData && projectionData.length > 0 && (
                  <div className="h-auto w-auto flex flex-col items-center">
                    <div className="w-full h-auto mb-3 py-2 text-base font-bold">
                      Earnings Projection{' '}
                    </div>
                    <div className="flex-1 w-full">
                      <Line data={projectionData} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {loading && (
              <div className="w-full flex min-h-[40dvh] justify-center items-center">
                <LuLoaderCircle className="animate-spin text-3xl" />
              </div>
            )}
          </Box>
        </div>
      )}
      <div className="w-full h-auto  px-1">
        <Box
          className="w-full h-auto rounded-xl mt-2"
          sx={{
            bgcolor:
              mode === 'dark' || (mode === 'system' && systemMode === 'dark')
                ? gray[800]
                : gray[100],
          }}
        >
          <div className="w-full h-auto p-6">
            <h3 className="text-lg font-bold mb-4">
              📚 Understanding Shardeum Node Rewards
            </h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm text-blue-200">
              <div>
                <h4 className="font-semibold text-white mb-2">
                  Node Types & Rewards:
                </h4>
                <ul className="space-y-1">
                  <li>
                    • <strong>Validator Nodes:</strong> Earn rewards when active
                    in consensus
                  </li>
                  <li>
                    • <strong>Archiver Nodes:</strong> Earn significantly more
                    (3.5x+) for data preservation
                  </li>
                  <li>
                    • <strong>Standby Nodes:</strong> Don't earn rewards but
                    rotate to active frequently
                  </li>
                  <li>
                    • <strong>Reward Formula:</strong> Node reward per hour ×
                    Active hours
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">
                  S:A Ratio Impact:
                </h4>
                <ul className="space-y-1">
                  <li>
                    • <strong>1:1 Ratio:</strong> ~50% active time
                  </li>
                  <li>
                    • <strong>2:1 Ratio:</strong> ~33% active time
                  </li>
                  <li>
                    • <strong>Higher Ratios:</strong> Less active time, more
                    competition
                  </li>
                  <li>
                    • <strong>Random Selection:</strong> Standby nodes selected
                    randomly, not FIFO
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">Key Features:</h4>
                <ul className="space-y-1">
                  <li>
                    • <strong>Capped Elastic Supply:</strong> Network controls
                    token issuance
                  </li>
                  <li>
                    • <strong>Manual Claiming:</strong> Must submit unstake
                    transaction to claim
                  </li>
                  <li>
                    • <strong>USD-Denominated Stake:</strong> Minimum stake in
                    USD value
                  </li>
                  <li>
                    • <strong>No Compounding:</strong> Min stake = max stake
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Box>
      </div>
    </Container>
  );
};
export const NodesRoute = createRoute({
  component: Nodes,
  getParentRoute: () => rootRoute,
  path: Routes.NODES,
});

export default Nodes;
